import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React from 'react';
import { PropTypes, describe } from 'react-desc';
import { aggregatedClasses, convertPropToCssClassName } from '@elliemae/ds-classnames';
import getComponentFromProps from '@elliemae/ds-utilities/getComponentFromProps';
import { DEFAULT_DELAY_CLOSE, DEFAULT_DELAY_OPEN } from '@elliemae/ds-shared/constants';
import DSPopper, { PopperPositions, PopperInteractions } from '@elliemae/ds-popper';
import { interactions } from '@elliemae/ds-popper/interaction';
import { positions } from '@elliemae/ds-popper/positions';
import { TooltipType } from './TooltipType.js';

var blockName = 'tooltip';
var TooltipContainer = aggregatedClasses('div')(blockName, false, false, {
  propsToRemoveFromFinalElement: ['isHovered', 'isDragged']
});
var TooltipText = aggregatedClasses('span')(blockName, 'text', false, {
  propsToRemoveFromFinalElement: ['isHovered', 'isDragged']
});

var DSTooltip = function DSTooltip(_ref) {
  var _ref$containerProps = _ref.containerProps,
      containerProps = _ref$containerProps === void 0 ? {} : _ref$containerProps,
      innerRef = _ref.innerRef,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? PopperPositions.TOP : _ref$placement,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title,
      _ref$delayClose = _ref.delayClose,
      delayClose = _ref$delayClose === void 0 ? DEFAULT_DELAY_CLOSE : _ref$delayClose,
      _ref$delayOpen = _ref.delayOpen,
      delayOpen = _ref$delayOpen === void 0 ? DEFAULT_DELAY_OPEN : _ref$delayOpen,
      _ref$interactionType = _ref.interactionType,
      interactionType = _ref$interactionType === void 0 ? PopperInteractions.HOVER : _ref$interactionType,
      _ref$triggerComponent = _ref.triggerComponent,
      triggerComponent = _ref$triggerComponent === void 0 ? null : _ref$triggerComponent,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? undefined : _ref$isOpen,
      _ref$onOpen = _ref.onOpen,
      onOpen = _ref$onOpen === void 0 ? function () {
    return null;
  } : _ref$onOpen,
      _ref$springAnimationC = _ref.springAnimationComponent,
      springAnimationComponent = _ref$springAnimationC === void 0 ? undefined : _ref$springAnimationC,
      _ref$tooltipType = _ref.tooltipType,
      tooltipType = _ref$tooltipType === void 0 ? TooltipType.TOOLTIP : _ref$tooltipType,
      _ref$showArrow = _ref.showArrow,
      showArrow = _ref$showArrow === void 0 ? true : _ref$showArrow,
      zIndex = _ref.zIndex,
      otherTooltipProps = _objectWithoutProperties(_ref, ["containerProps", "innerRef", "placement", "title", "delayClose", "delayOpen", "interactionType", "triggerComponent", "className", "isOpen", "onOpen", "springAnimationComponent", "tooltipType", "showArrow", "zIndex"]);

  var _convertPropToCssClas = convertPropToCssClassName(blockName),
      tooltipBlockName = _convertPropToCssClas.cssClassName;

  var Content = title;

  if (typeof title === 'string') {
    Content = /*#__PURE__*/React.createElement(TooltipText, null, title);
  }

  return /*#__PURE__*/React.createElement(DSPopper, {
    blockName: tooltipBlockName,
    contentComponent: /*#__PURE__*/React.createElement(TooltipContainer, _extends({}, containerProps, otherTooltipProps, {
      className: "tooltip-container--".concat(tooltipType, " ").concat(className),
      innerRef: innerRef
    }), Content),
    delayClose: delayClose,
    delayOpen: delayOpen,
    interactionType: interactionType,
    isOpen: isOpen,
    onOpen: onOpen,
    placement: placement,
    showArrow: tooltipType === TooltipType.TOOLTIP ? showArrow : false,
    springAnimationComponent: springAnimationComponent,
    triggerComponent: getComponentFromProps(triggerComponent),
    zIndex: zIndex
  });
};

var tooltipProps = {
  containerProps: PropTypes.object.description('Set of Properties attached to the main container'),
  placement: PropTypes.oneOf(positions).description('Tooltip position').defaultValue(PopperPositions.TOP),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).description('Tooltip content').isRequired,
  delayClose: PropTypes.string.description('Delay to close the tooltip').defaultValue(DEFAULT_DELAY_CLOSE),
  delayOpen: PropTypes.string.description('Delay to open the tooltip ').defaultValue(DEFAULT_DELAY_OPEN),
  interactionType: PropTypes.oneOf(interactions).description('A type indicating how to open/close the tooltip').defaultValue(PopperInteractions.HOVER),
  triggerComponent: PropTypes.element.description('The component that will open/close the tooltip').isRequired,
  isOpen: PropTypes.bool.description('Whether the tooltip is visible or not').defaultValue(undefined),
  onOpen: PropTypes.func.description('Handler when the tooltip opens'),
  springAnimationComponent: PropTypes.element.description('Spring animation component'),
  showArrow: PropTypes.bool.description('Whether to show an arrow or not').defaultValue(true),
  className: PropTypes.string.description('css class'),
  tooltipType: PropTypes.oneOf(['tooltip', 'toolbar']).description('Type of tooltip').defaultValue(TooltipType.TOOLTIP),
  zIndex: PropTypes.number.description('z-index value assigned to the tooltip').defaultValue(undefined),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).description('Ref to the tooltip container element')
};
DSTooltip.propTypes = tooltipProps;
var TooltipWithSchema = describe(DSTooltip);
TooltipWithSchema.propTypes = tooltipProps;

export default DSTooltip;
export { DSTooltip, TooltipContainer, TooltipText, TooltipWithSchema };
//# sourceMappingURL=DSTooltip.js.map
